import { useLayerStore } from '@/store/use-layer-store';
import { useWatchfaceStore } from '@/store/use-watchface-store';
import { getWatchfaceArea } from './use-watchface-devices';
import { Shape, ShapeType } from '@/services/shape';
import {
  PowerClassRule,
  PowerClassRuleType,
  getWatchfacePowerRules
} from '@/resources/power-class-rules';
import { useWatchfaceScreenShot } from './use-watchface-screenshot';
import { onUnmounted, ref } from 'vue';
import { round } from 'lodash-es';
import { getCanvasGrayValue } from '@/utils/canvas/canvas.utils';
import {
  getImageNotTransparentAreaByWorker,
  terminateWebworkers
} from '@/utils/web-worker/image-not-transparent';
import { useImagesStore } from '@/store/use-images-store';
import { ResourceKey } from '@/services/shape/attributes';
import { useWatchfaceAndroid } from './use-watchface-android';
import { useShapeTransform } from './use-shape-transform';

export interface LayerPowerClass {
  themeId: string;
  shapeId: string;
  colorId: string;
  ruleType: PowerClassRuleType;
  rule?: PowerClassRule;
  fps: number;
  areaRatio: number;
  grayScale: number;
  powerClass: 1 | 2 | 3;
  desc?: string;
}

export interface WatchfacePowerData {
  themeId: string;
  colorId: string;
  powerClass: number;
  highPowerLayers: LayerPowerClass[];
  reasons?: string[];
}

const _watchfacePowerData = ref<WatchfacePowerData>();

const _maxAreaCached = new Map<string, number>();
const _getImagesMaxArea = async (
  images: {
    basename: string;
    base64: string;
  }[],
  width: number,
  height: number
) => {
  let maxArea = 0;
  // 使用promise all
  const step = 4;
  let index = 0;
  const promises: Promise<void>[] = [];
  // 请将images按step分组
  while (index < images.length) {
    promises.length = 0;
    for (let i = index; i < index + step; i++) {
      if (i >= images.length) break;
      const webWorkerIndex = i % step;
      const { base64: image, basename } = images[i];
      if (!image) continue;

      if (_maxAreaCached.has(basename)) {
        maxArea = Math.max(_maxAreaCached.get(basename) ?? 0, maxArea);
        continue;
      }
      // 判断是否已缓存
      promises.push(
        (async () => {
          try {
            const area =
              (await getImageNotTransparentAreaByWorker({
                imageUrl: image,
                index: webWorkerIndex,
                width: width,
                height: height
              })) ?? 0;
            _maxAreaCached.set(basename, area);
            maxArea = Math.max(maxArea, area);
          } catch (error) {
            console.log(0);
          }
        })()
      );
    }
    await Promise.all(promises);
    index += step;
  }
  return maxArea;
};

export const useWatchfacePowerClass = () => {
  const { screenshot } = useWatchfaceScreenShot();
  const watchfaceStore = useWatchfaceStore();
  const layerStore = useLayerStore();
  const imageStore = useImagesStore();

  /**
   * 获取表盘灰度值(不考虑息屏表盘)
   * @returns
   */
  const _getThemeGrayScale = async (
    theme: StoreThemeItem,
    colorVal: string
  ) => {
    const canvas = await screenshot(undefined, {
      themeName: theme.name,
      themeType: theme.type,
      themeColor: colorVal,
      forceRenderWhenHidden: true,
      returnType: 'canvas'
    });
    const grayscale = canvas
      ? await getCanvasGrayValue(canvas as HTMLCanvasElement)
      : 0;

    // 取整
    const _grayScale = Math.ceil(grayscale);
    return _grayScale;
  };

  const _getAreaRatio = async (shape: Shape, colorId: string) => {
    const _watchfaceArea = getWatchfaceArea(
      watchfaceStore.description.deviceType ?? ''
    );
    const {
      w = 0,
      h = 0,
      spriteType = 'sprite',
      source,
      scale = 1
    } = shape.attrs;

    let _area = w * h;

    let resourceName: ResourceKey | undefined;
    // 如果是图片类型的序列帧需要计算序列帧非黑像素面积
    if (shape.type == ShapeType.Sprite && spriteType == 'sprite') {
      resourceName = 'images';
    } else if (shape.type == ShapeType.Image && source == 'timeSecond') {
      resourceName = 'pointer0';
    }

    if (resourceName) {
      const images = shape
        .getResources({
          resourceName: resourceName,
          colorGroup: colorId
        })
        .map((item) => {
          return {
            basename: item,
            base64: imageStore.imagesMap[item]?.value ?? ''
          };
        });
      _area = await _getImagesMaxArea(images, w, h);
    }

    // 考虑图片缩放值
    if (scale != 1 && useShapeTransform().canuseScale(shape)) {
      _area = _area * scale * scale;
    }

    const _areaRatio = round(Math.min((_area / _watchfaceArea) * 100, 100), 2);
    return _areaRatio;
  };

  const _getThemeAllLayersPowerClass = async () => {
    // 获取当前设备的功耗规则
    const powerClassRules = getWatchfacePowerRules(
      watchfaceStore.description.deviceType ?? ''
    );
    if (!powerClassRules) return;

    const { androidWatcfaceOS } = useWatchfaceAndroid();

    const _layersPowerData: {
      [themeId: string]: {
        [colorId: string]: LayerPowerClass[];
      };
    } = {};

    // 计算表盘灰度 以第一个样式第一个配色灰度为准
    const firstTheme = layerStore.themes.find((item) => item.type == 'normal');
    if (!firstTheme) return;
    const _grayScale = await _getThemeGrayScale(
      firstTheme,
      firstTheme.colorGroupTable[0]?.color ?? '#ffffff'
    );

    for (const theme of layerStore.themes.filter(
      (item) => item.type !== 'AOD'
    )) {
      // 计算每个样式每个配色下图层功耗信息
      if (!_layersPowerData[theme.id]) _layersPowerData[theme.id] = {};

      // 这里只需要处理第一个配色即可，其他配色不需要处理
      for (const color of [theme.colorGroupTable[0]]) {
        const colorId = color.id;
        // ensure theme color
        if (!_layersPowerData[theme.id][colorId])
          _layersPowerData[theme.id][colorId] = [];
        const colorRes = _layersPowerData[theme.id][colorId];

        let existsCompassLayer = false;
        const layerItems: StoreLayerItem[] = [...theme.children];
        while (layerItems.length) {
          const layer = layerItems.shift();
          if (!layer) continue;

          // 如果有子元素，比如widget或slot，直接计算内部元素的功耗，不计算widget
          if (layer.children?.length) {
            layerItems.push(...(layer.children ?? []));
            continue;
          }
          const shape = Shape.getShape(layer.id);
          if (!shape) continue;

          const { type, attrs } = shape;
          // 如果是指南针图层，则不计算功耗
          if (
            type == ShapeType.Image &&
            attrs.source == 'systemSensorCompass'
          ) {
            existsCompassLayer = true;
            continue;
          }

          // 默认ruleType为default
          let ruleType: PowerClassRuleType = 'default';
          let isVideoSprite = false;
          if (shape.type == ShapeType.JsFile) {
            ruleType = 'js-lua';
          } else if (type == ShapeType.Image && attrs.source == 'timeSecond') {
            ruleType = 'secondPointer';
          } else if (type == ShapeType.Sprite) {
            ruleType = 'sprite';
          } else if (type == ShapeType.Rive) {
            ruleType = 'rive';
          } else if (type == ShapeType.VideoWatchface) {
            ruleType = 'sprite';
          }

          // 由于序列帧和视频使用相同的规则，如果是序列帧，需要进一步判断是否为视频相关
          if (
            ruleType == 'sprite' &&
            // 视频类的序列帧
            (attrs.spriteType == 'video' || type == ShapeType.VideoWatchface)
          ) {
            isVideoSprite = true;
            // 如果是视频序列帧：智能表且是轻智能,不可使用视频，仅能用一种预览图代替
            if (androidWatcfaceOS.value == 'vela') ruleType = 'default';
          }

          // 面积百分比（100%）
          const { pointerFps = 1, spriteFps = 1, videoFps = 30 } = attrs;
          const _areaRatio = await _getAreaRatio(shape, colorId);

          // 获取图层帧率
          let fps = 0;
          if (ruleType == 'secondPointer') {
            fps = pointerFps;
          } else if (ruleType == 'sprite') {
            fps = isVideoSprite ? videoFps : spriteFps;
          }

          const layerPowerClass: LayerPowerClass = {
            themeId: theme.id,
            shapeId: layer.id,
            colorId,
            grayScale: _grayScale,
            areaRatio: _areaRatio,
            fps,
            powerClass: 1,
            ruleType
          };

          // 查找所有符合要求规则中功耗最大的一个
          const rule = powerClassRules[ruleType]
            .filter((item) => {
              return (
                item.areaRatio(_areaRatio) &&
                item.fps(fps)
              );
            })
            .sort((a, b) => b.powerClass - a.powerClass)?.[0];

          layerPowerClass.powerClass = rule?.powerClass ?? 1;
          layerPowerClass.desc = rule?.desc ?? '';
          layerPowerClass.rule = rule;
          colorRes.push(layerPowerClass);
        }
        // 对colorRes进行排序
        colorRes.sort((a, b) => b.powerClass - a.powerClass);

        // 如果存在指南针
        // 获取指南针图层
        if (existsCompassLayer) {
          // 此时具体配色下的所有图层功耗信息已计算完成，此时，应该将指南针加上
          // 注意指南针只能加一次，需要判断加在哪个图层上
          // 添加规则：在最影响功耗的图层上添加指南针

          // 对colorRes进行排序
          colorRes.sort(
            (a, b) =>
              (b.rule?.powerClassCompass ?? b.powerClass) -
              (a.rule?.powerClassCompass ?? a.powerClass)
          );
          const item = colorRes[0];
          const powerClassCompass = item.rule?.powerClassCompass ?? 0;
          if (powerClassCompass > item.powerClass) {
            item.powerClass = Math.min(3, powerClassCompass) as 1;
            item.desc = '由于表盘存在指南针元素，导致功耗较高';
          }
        }
      }
      //
    }
    return _layersPowerData;
  };

  /**
   * 获取某一theme某一color下的最终功耗
   * @param themeColorLayersClassData
   * @returns
   */
  const _getThemeColorPowerClass = (
    themeColorLayersClassData: LayerPowerClass[]
  ) => {
    /**
     * 规则：
     * 1. 只要有3级图层，则为3级；
     * 2. 2级图层数量<1时，则为2级；
     * 3. 2级图层数量>1时，平滑指针，2-3个2级按照2级处理。非平滑指针按3级处理；
     * 3. 没有以上情况下，则为1级；
     */
    // 获取3级的元素
    const powerClass3Layers = themeColorLayersClassData.filter(
      (item) => item.powerClass == 3
    );
    const powerClass3LayerCount = powerClass3Layers.length;
    // 获取2级的元素
    const powerClass2Layers = themeColorLayersClassData.filter(
      (item) => item.powerClass == 2
    );
    // 2级的元素：平滑指针的数量
    const smoothPointerLayers = themeColorLayersClassData.filter(
      (item) =>
        item.powerClass == 2 &&
        item.ruleType == 'secondPointer' &&
        // 帧率>1的为平滑指针
        item.fps > 1
    );
    // 3个以内的平滑指针=1个指针处理
    const powerClass2LayerCount =
      powerClass2Layers.length -
      Math.max(1, Math.min(smoothPointerLayers.length, 3)) +
      1;

    if (
      // 只要有3级图层，则为3级；
      powerClass3LayerCount
    ) {
      return {
        powerClass: 3,
        highPowerLayers: powerClass3Layers,
        reasons: powerClass3Layers.map((item) => item.desc ?? '')
      };
    } else if (
      // 2级图层数量只有1个时，则为2级；
      powerClass2LayerCount == 1
    ) {
      return {
        powerClass: 2,
        highPowerLayers: powerClass2Layers
      };
    } else if (
      // 2级图层数量>=2个时
      powerClass2LayerCount > 1
    ) {
      return {
        powerClass: 3,
        highPowerLayers: powerClass2Layers,
        reasons: ['两个及以上2级元素，导致表盘功耗为3级。']
      };
    } else {
      // 其他情况：均为1级
      return {
        powerClass: 1,
        highPowerLayers: []
      };
    }
  };
  const _resetWatchfacePowerData = async () => {
    const themeLayersPowerClass = await _getThemeAllLayersPowerClass();
    // 此时是多个样式、多个配色的集合，需要把每个样式和配色的单独区分出来
    if (!themeLayersPowerClass) return;

    const res: {
      [themeId: string]: WatchfacePowerData;
    } = {};
    Object.entries(themeLayersPowerClass).forEach((item) => {
      const [themeId, colors] = item;
      Object.entries(colors).forEach((colorItem) => {
        const [colorId, themeColorLayersClassData] = colorItem;
        // ensure
        if (!res[themeId]) {
          res[themeId] = {
            themeId,
            colorId: colorId,
            powerClass: 1,
            highPowerLayers: []
          };
        }
        res[themeId] = {
          ...res[themeId],
          ..._getThemeColorPowerClass(themeColorLayersClassData)
        };
      });
    });
    return res;
  };

  const timer = ref(0);

  const resetWatchfacePowerData = async () => {
    let powerData: WatchfacePowerData | undefined = undefined;
    try {
      const res = await _resetWatchfacePowerData();
      Object.entries(res ?? {}).forEach((item) => {
        const [, colorRes] = item;
        const { powerClass } = colorRes;
        if (powerClass > (powerData?.powerClass ?? 0)) {
          powerData = colorRes;
        }
      });
    } catch (error) {
      console.error(error);
    }
    _watchfacePowerData.value = powerData;

    if (timer.value) clearTimeout(timer.value);
    timer.value = window.setTimeout(() => {
      terminateWebworkers();
    }, 3000);

    return powerData;
  };

  onUnmounted(() => clearTimeout(timer.value));

  const clear = () => {
    _maxAreaCached.clear();
  };

  return {
    resetWatchfacePowerData,
    watchfacePowerData: _watchfacePowerData,
    clear
  };
};
